import React from "react";
import { ConfigProvider } from "antd";
import { useTheme } from "../../context/ThemeContext";
import {
  PaginationContainer,
  PaginationLeft,
  CombinedLabel,
  PaginationCenter,
  PaginationRight,
  LinesPerPageLabel,
  SelectWrapper,
  StyledSelect,
  SelectIcon,
  PaginationStyled,
  SelectDropdownGlobalStyle,
} from "./Style";
import { TablePaginationProps } from "./types";

/**
 * Common Table Pagination Component
 * A reusable pagination component with total count, range, and page size selector
 *
 * @param totalCount - Total number of items
 * @param currentPage - Current page number
 * @param pageSize - Number of items per page
 * @param onChange - Callback when page changes (page, size)
 * @param totalLabel - Label for total count (default: "Total records")
 * @param pageSizeOptions - Page size options (default: [10, 20, 30, 50, 100])
 * @param defaultPageSize - Default page size (default: 20)
 * @param padding - Custom padding (default: "16px 12px")
 */
const TablePagination: React.FC<TablePaginationProps> = ({
  totalCount,
  currentPage,
  pageSize,
  onChange,
  totalLabel = "Total records",
  pageSizeOptions = [10, 20, 30, 50, 100],
  defaultPageSize = 20,
  padding,
}) => {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? "dark" : "light";
  
  const safeTotalCount = totalCount ?? 0;
  const safePageSize = pageSize && pageSize > 0 ? pageSize : defaultPageSize;
  const safeCurrentPage = currentPage && currentPage > 0 ? currentPage : 1;

  const calculatedStartRange = (safeCurrentPage - 1) * safePageSize + 1;
  const calculatedEndRange = safeCurrentPage * safePageSize;
  const startRange = safeTotalCount > 0 ? calculatedStartRange : 0;
  const endRange =
    safeTotalCount > 0 ? Math.min(calculatedEndRange, safeTotalCount) : 0;

  const handlePageSizeChange = (value: string) => {
    const newSize = parseInt(value, 10);
    onChange(1, newSize);
  };

  const handlePageChange = (page: number) => {
    onChange(page, safePageSize);
  };

  return (
    <>
      <SelectDropdownGlobalStyle isDarkMode={isDarkMode} />
      <ConfigProvider
        theme={{
          components: {
            Select: {
              colorBgContainer: isDarkMode ? "#2D3748" : "#ffffff",
              colorText: isDarkMode ? "#ffffff" : "#4e5156",
              colorBorder: isDarkMode ? "#4A5568" : "#d9d9d9",
              optionSelectedBg: isDarkMode ? "#4a5568" : "#E6F4FF",
              optionActiveBg: isDarkMode ? "#4a5568" : "#E6F4FF",
              colorTextQuaternary: isDarkMode ? "#ffffff" : "#bfbfbf",
              colorTextTertiary: isDarkMode ? "#ffffff" : "#8c8c8c",
            },
          },
        }}
      >
        <PaginationContainer padding={padding} theme={theme}>
        <PaginationLeft>
          <CombinedLabel theme={theme}>
            {totalLabel}: {safeTotalCount} | Showing {startRange}-{endRange}
          </CombinedLabel>
        </PaginationLeft>
        <PaginationCenter>
          <PaginationStyled
            padding="0px 0px"
            marginTop="0px"
            borderTop="0px"
            theme={theme}
            onChange={handlePageChange}
            total={safeTotalCount}
            pageSize={safePageSize}
            current={safeCurrentPage}
            defaultPageSize={defaultPageSize}
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={() => null}
          />
        </PaginationCenter>
        <PaginationRight>
          <LinesPerPageLabel theme={theme}>Lines per page</LinesPerPageLabel>
          <SelectWrapper>
            <StyledSelect
              theme={theme}
              value={String(safePageSize)}
              onChange={handlePageSizeChange}
              suffixIcon={null}
              getPopupContainer={(trigger) =>
                trigger.parentElement || document.body
              }
            >
              {pageSizeOptions.map((option) => (
                <StyledSelect.Option key={option} value={String(option)}>
                  {option}
                </StyledSelect.Option>
              ))}
            </StyledSelect>
            <SelectIcon theme={theme} />
          </SelectWrapper>
        </PaginationRight>
      </PaginationContainer>
    </ConfigProvider>
    </>
  );
};

export default TablePagination;


